import { useState, useEffect } from 'react';

interface Props {
  visible: boolean;
  theme?: 'tech' | 'pink' | 'white';
}

const STAGES = [
  { id: 1, icon: 'ri-file-list-3-line',   text: '解析你的租屋條件', sub: 'Parsing requirements...' },
  { id: 2, icon: 'ri-database-2-line',     text: '掃描全台房源資料庫', sub: 'Scanning 10,000+ listings...' },
  { id: 3, icon: 'ri-cpu-line',            text: 'AI 深度語意比對', sub: 'Running semantic matching...' },
  { id: 4, icon: 'ri-git-network-line',    text: '建立專屬配對模型', sub: 'Building your profile...' },
  { id: 5, icon: 'ri-notification-3-line', text: '啟動即時通知監控', sub: 'Activating alert engine...' },
];

// 固定偽隨機資料流節點（不用 Math.random，避免 hydration 問題）
const DATA_NODES = [
  { x: 20, y: 25, delay: 0,    size: 3 },
  { x: 75, y: 15, delay: 400,  size: 2 },
  { x: 85, y: 55, delay: 200,  size: 4 },
  { x: 15, y: 70, delay: 600,  size: 2 },
  { x: 60, y: 80, delay: 100,  size: 3 },
  { x: 40, y: 10, delay: 800,  size: 2 },
  { x: 90, y: 30, delay: 300,  size: 3 },
  { x: 10, y: 45, delay: 500,  size: 2 },
  { x: 55, y: 90, delay: 700,  size: 4 },
  { x: 30, y: 85, delay: 150,  size: 2 },
  { x: 70, y: 60, delay: 900,  size: 3 },
  { x: 45, y: 35, delay: 250,  size: 2 },
];

const SCAN_LINES = [
  { x1: 20, y1: 25, x2: 50, y2: 50, delay: 200 },
  { x1: 75, y1: 15, x2: 50, y2: 50, delay: 400 },
  { x1: 85, y1: 55, x2: 50, y2: 50, delay: 600 },
  { x1: 15, y1: 70, x2: 50, y2: 50, delay: 300 },
  { x1: 60, y1: 80, x2: 50, y2: 50, delay: 700 },
  { x1: 90, y1: 30, x2: 50, y2: 50, delay: 100 },
  { x1: 10, y1: 45, x2: 50, y2: 50, delay: 500 },
  { x1: 70, y1: 60, x2: 50, y2: 50, delay: 800 },
];

export default function AiMatchingOverlay({ visible, theme = 'tech' }: Props) {
  const [currentStage, setCurrentStage] = useState(0);
  const [nodesVisible, setNodesVisible] = useState<number[]>([]);
  const [linesVisible, setLinesVisible] = useState<number[]>([]);

  const accent = theme === 'pink' ? '#fb7185' : theme === 'white' ? '#374151' : '#2dd4bf';
  const accentDim = theme === 'pink' ? 'rgba(251,113,133,0.15)' : theme === 'white' ? 'rgba(55,65,81,0.15)' : 'rgba(45,212,191,0.15)';
  const accentText = theme === 'pink' ? 'text-rose-400' : theme === 'white' ? 'text-gray-300' : 'text-teal-400';
  const accentBorder = theme === 'pink' ? 'border-rose-400/40' : theme === 'white' ? 'border-gray-400/40' : 'border-teal-400/40';
  const accentBg = theme === 'pink' ? 'bg-rose-400/10' : theme === 'white' ? 'bg-gray-400/10' : 'bg-teal-400/10';

  useEffect(() => {
    if (!visible) {
      setCurrentStage(0);
      setNodesVisible([]);
      setLinesVisible([]);
      return;
    }

    // 階段推進
    const timers: ReturnType<typeof setTimeout>[] = [];
    const stageDurations = [900, 1400, 1200, 900, 600];
    let acc = 300;
    stageDurations.forEach((d, i) => {
      timers.push(setTimeout(() => setCurrentStage(i + 1), acc));
      acc += d;
    });

    // 節點出現
    DATA_NODES.forEach((_, i) => {
      timers.push(setTimeout(() => {
        setNodesVisible(prev => [...prev, i]);
      }, 400 + i * 180));
    });

    // 線條出現
    SCAN_LINES.forEach((_, i) => {
      timers.push(setTimeout(() => {
        setLinesVisible(prev => [...prev, i]);
      }, 1200 + i * 220));
    });

    return () => timers.forEach(clearTimeout);
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(3,7,18,0.92)', backdropFilter: 'blur(8px)' }}
    >
      {/* CSS 動畫 keyframes */}
      <style>{`
        @keyframes radarSweep {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes pulse-ring {
          0%   { transform: scale(0.8); opacity: 0.8; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        @keyframes scan-line {
          0%   { opacity: 0; transform: scaleX(0); }
          30%  { opacity: 1; transform: scaleX(1); }
          100% { opacity: 0.35; transform: scaleX(1); }
        }
        @keyframes node-appear {
          0%   { opacity: 0; transform: scale(0) translate(-50%, -50%); }
          60%  { opacity: 1; transform: scale(1.4) translate(-36%, -36%); }
          100% { opacity: 0.8; transform: scale(1) translate(-50%, -50%); }
        }
        @keyframes data-scroll {
          0%   { transform: translateY(0); }
          100% { transform: translateY(-50%); }
        }
        @keyframes stage-enter {
          0%   { opacity: 0; transform: translateX(-12px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        @keyframes glow-pulse {
          0%, 100% { box-shadow: 0 0 20px currentColor, 0 0 40px currentColor; opacity: 0.6; }
          50%       { box-shadow: 0 0 40px currentColor, 0 0 80px currentColor; opacity: 1; }
        }
        @keyframes center-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%       { transform: scale(1.08); opacity: 0.85; }
        }
        @keyframes orbit-rotate {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes orbit-counter {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(-360deg); }
        }
        @keyframes typewriter {
          from { width: 0; }
          to   { width: 100%; }
        }
      `}</style>

      <div className="relative w-full max-w-lg mx-4">

        {/* 頂部系統標頭 */}
        <div className="flex items-center justify-between mb-6 px-1">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-red-500"></div>
            <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: accent }}></div>
          </div>
          <p className="text-xs font-mono tracking-widest" style={{ color: accent, opacity: 0.7 }}>
            AI_MATCH_ENGINE v2.1
          </p>
          <div className="flex items-center gap-1.5">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: accent }}></span>
              <span className="relative inline-flex rounded-full h-2 w-2" style={{ backgroundColor: accent }}></span>
            </span>
            <span className="text-xs font-mono" style={{ color: accent }}>LIVE</span>
          </div>
        </div>

        {/* 主要卡片 */}
        <div
          className={`rounded-3xl border p-6 relative overflow-hidden ${accentBorder}`}
          style={{ background: 'rgba(8,14,30,0.95)' }}
        >
          {/* 背景網格 */}
          <div className="absolute inset-0 pointer-events-none" style={{
            backgroundImage: `linear-gradient(${accentDim} 1px, transparent 1px), linear-gradient(90deg, ${accentDim} 1px, transparent 1px)`,
            backgroundSize: '30px 30px',
            opacity: 0.4,
          }}></div>

          {/* 角落裝飾 */}
          {[
            'top-0 left-0 border-t-2 border-l-2 rounded-tl-3xl w-8 h-8',
            'top-0 right-0 border-t-2 border-r-2 rounded-tr-3xl w-8 h-8',
            'bottom-0 left-0 border-b-2 border-l-2 rounded-bl-3xl w-8 h-8',
            'bottom-0 right-0 border-b-2 border-r-2 rounded-br-3xl w-8 h-8',
          ].map((cls, i) => (
            <div key={i} className={`absolute pointer-events-none ${cls}`} style={{ borderColor: accent, opacity: 0.6 }}></div>
          ))}

          {/* 上半：雷達 + 資料節點 */}
          <div className="flex gap-6 items-center mb-6 relative z-10">

            {/* 雷達圓 */}
            <div className="relative flex-shrink-0 w-44 h-44">
              {/* 外擴漣漪環 */}
              {[0, 1].map(i => (
                <div key={i} className="absolute inset-0 rounded-full border" style={{
                  borderColor: accent,
                  animation: `pulse-ring 2s ease-out ${i * 1}s infinite`,
                  opacity: 0.3,
                }}></div>
              ))}

              {/* 靜態同心圓 */}
              {[0.9, 0.7, 0.5, 0.3].map((scale, i) => (
                <div key={i} className="absolute rounded-full border" style={{
                  borderColor: accent,
                  opacity: 0.12 + i * 0.05,
                  width: `${scale * 100}%`,
                  height: `${scale * 100}%`,
                  top: `${(1 - scale) * 50}%`,
                  left: `${(1 - scale) * 50}%`,
                }}></div>
              ))}

              {/* 十字準星 */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-full h-px" style={{ backgroundColor: accent, opacity: 0.15 }}></div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="h-full w-px" style={{ backgroundColor: accent, opacity: 0.15 }}></div>
              </div>

              {/* 雷達掃描扇形 */}
              <div className="absolute inset-0" style={{ animation: 'radarSweep 2.4s linear infinite' }}>
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  <defs>
                    <radialGradient id="radarGrad" cx="50%" cy="50%">
                      <stop offset="0%" stopColor={accent} stopOpacity="0.8" />
                      <stop offset="100%" stopColor={accent} stopOpacity="0" />
                    </radialGradient>
                  </defs>
                  <path
                    d="M50 50 L50 5 A45 45 0 0 1 90 50 Z"
                    fill="url(#radarGrad)"
                    opacity="0.6"
                  />
                  <line x1="50" y1="50" x2="50" y2="5" stroke={accent} strokeWidth="1.5" opacity="0.9" />
                </svg>
              </div>

              {/* 節點 SVG 層 */}
              <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
                {/* 連線 */}
                {SCAN_LINES.map((line, i) => (
                  linesVisible.includes(i) && (
                    <line
                      key={i}
                      x1={line.x1} y1={line.y1}
                      x2={line.x2} y2={line.y2}
                      stroke={accent}
                      strokeWidth="0.4"
                      opacity="0.3"
                      strokeDasharray="2 2"
                    />
                  )
                ))}
                {/* 節點 */}
                {DATA_NODES.map((node, i) => (
                  nodesVisible.includes(i) && (
                    <g key={i}>
                      <circle cx={node.x} cy={node.y} r={node.size + 2} fill={accent} opacity="0.08" />
                      <circle cx={node.x} cy={node.y} r={node.size} fill={accent} opacity="0.7" />
                    </g>
                  )
                ))}
              </svg>

              {/* 中央核心 */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div
                  className="relative w-14 h-14 flex items-center justify-center rounded-full"
                  style={{
                    background: `radial-gradient(circle, ${accentDim} 0%, transparent 70%)`,
                    animation: 'center-pulse 2s ease-in-out infinite',
                  }}
                >
                  {/* 軌道環 */}
                  <div className="absolute w-12 h-12 rounded-full border border-dashed" style={{
                    borderColor: accent,
                    opacity: 0.3,
                    animation: 'orbit-rotate 6s linear infinite',
                  }}></div>
                  <div className="absolute w-9 h-9 rounded-full border" style={{
                    borderColor: accent,
                    opacity: 0.2,
                    animation: 'orbit-counter 4s linear infinite',
                  }}></div>
                  <i className="ri-robot-2-fill text-2xl relative z-10" style={{ color: accent }}></i>
                </div>
              </div>
            </div>

            {/* 右側：資料串流 */}
            <div className="flex-1 overflow-hidden h-44 relative">
              <div className="absolute inset-0 pointer-events-none z-10"
                style={{ background: `linear-gradient(to bottom, rgba(8,14,30,0.9), transparent 20%, transparent 80%, rgba(8,14,30,0.9))` }}
              ></div>
              <div style={{ animation: 'data-scroll 8s linear infinite' }}>
                {[
                  '台北市 大安區 ✓', '月租 ≤ NT$25,000', '2房 / 住宅', 'Pet OK ✗',
                  '新北市 板橋 ✓', '月租 ≤ NT$18,000', '套房 ✓', '坪數 ≥ 10坪',
                  '台中市 西屯 ✓', 'AI Score: 92.4', 'Similarity: 0.87', 'Match: HIGH',
                  '台北市 大安區 ✓', '月租 ≤ NT$25,000', '2房 / 住宅', 'Pet OK ✗',
                  '新北市 板橋 ✓', '月租 ≤ NT$18,000', '套房 ✓', '坪數 ≥ 10坪',
                ].map((line, i) => (
                  <div key={i} className="flex items-center gap-2 py-1 border-b" style={{ borderColor: `${accent}18` }}>
                    <span className="text-[10px] font-mono w-4 flex-shrink-0" style={{ color: accent, opacity: 0.4 }}>
                      {String(i % 10).padStart(2, '0')}
                    </span>
                    <span className="text-[11px] font-mono truncate" style={{
                      color: line.includes('✓') ? '#4ade80' : line.includes('✗') ? '#f87171' : line.includes('Score') ? '#fbbf24' : accent,
                      opacity: 0.85,
                    }}>
                      {line}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 進度條 */}
          <div className="mb-5 relative z-10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-mono" style={{ color: accent, opacity: 0.7 }}>MATCHING PROGRESS</span>
              <span className="text-xs font-mono font-bold" style={{ color: accent }}>
                {Math.round((currentStage / STAGES.length) * 100)}%
              </span>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: `${accent}20` }}>
              <div
                className="h-full rounded-full transition-all duration-700 ease-out"
                style={{
                  width: `${(currentStage / STAGES.length) * 100}%`,
                  background: `linear-gradient(90deg, ${accent}80, ${accent})`,
                  boxShadow: `0 0 12px ${accent}`,
                }}
              ></div>
            </div>
          </div>

          {/* 階段列表 */}
          <div className="space-y-2 relative z-10">
            {STAGES.map((stage, i) => {
              const state = i + 1 < currentStage ? 'done' : i + 1 === currentStage ? 'active' : 'pending';
              return (
                <div
                  key={stage.id}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-500"
                  style={{
                    background: state === 'active' ? accentDim : 'transparent',
                    border: `1px solid ${state === 'active' ? accent : state === 'done' ? `${accent}30` : 'transparent'}`,
                    opacity: state === 'pending' ? 0.35 : 1,
                    animation: state === 'active' ? 'stage-enter 0.3s ease-out' : undefined,
                  }}
                >
                  {/* 狀態指示 */}
                  <div className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-lg" style={{
                    background: state === 'done' ? `${accent}25` : state === 'active' ? `${accent}30` : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${state !== 'pending' ? accent : 'rgba(255,255,255,0.1)'}`,
                    opacity: state !== 'pending' ? 1 : 0.5,
                  }}>
                    {state === 'done' ? (
                      <i className="ri-check-line text-sm" style={{ color: accent }}></i>
                    ) : state === 'active' ? (
                      <i className={`${stage.icon} text-sm animate-pulse`} style={{ color: accent }}></i>
                    ) : (
                      <i className={`${stage.icon} text-sm`} style={{ color: accent, opacity: 0.4 }}></i>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold leading-none truncate" style={{
                      color: state !== 'pending' ? '#fff' : 'rgba(255,255,255,0.3)',
                    }}>
                      {stage.text}
                    </p>
                    {state === 'active' && (
                      <p className="text-[10px] font-mono mt-0.5 truncate" style={{ color: accent, opacity: 0.7 }}>
                        {stage.sub}
                      </p>
                    )}
                  </div>

                  {state === 'active' && (
                    <div className="flex gap-0.5 flex-shrink-0">
                      {[0, 1, 2].map(d => (
                        <div key={d} className="w-1 h-3 rounded-full animate-bounce" style={{
                          backgroundColor: accent,
                          animationDelay: `${d * 0.15}s`,
                          opacity: 0.8,
                        }}></div>
                      ))}
                    </div>
                  )}
                  {state === 'done' && (
                    <span className="text-[10px] font-mono flex-shrink-0" style={{ color: accent, opacity: 0.6 }}>DONE</span>
                  )}
                </div>
              );
            })}
          </div>

          {/* 底部頁腳 */}
          <div className="mt-5 pt-4 border-t flex items-center justify-between relative z-10" style={{ borderColor: `${accent}20` }}>
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: accent }}></div>
              <span className="text-[10px] font-mono" style={{ color: accent, opacity: 0.5 }}>ENCRYPTING YOUR DATA</span>
            </div>
            <span className="text-[10px] font-mono" style={{ color: accent, opacity: 0.4 }}>
              藍舍AI地產 © 2026
            </span>
          </div>
        </div>

        {/* 底部掃描條紋 */}
        <div className="mt-4 flex justify-center gap-1">
          {Array.from({ length: 24 }).map((_, i) => (
            <div
              key={i}
              className="h-1 rounded-full"
              style={{
                width: i % 3 === 0 ? '16px' : i % 3 === 1 ? '8px' : '4px',
                backgroundColor: accent,
                opacity: i <= (currentStage / STAGES.length) * 24 ? 0.7 : 0.1,
                transition: 'opacity 0.3s ease',
              }}
            ></div>
          ))}
        </div>
      </div>
    </div>
  );
}
